/**
 * =============================================================================
 * TenantSeoDefaults.tsx - Tag SEO sitewide tenant-aware
 * =============================================================================
 *
 * Inietta a runtime, via react-helmet-async:
 *  - <meta property="og:site_name"> con il nome del tenant corrente
 *  - JSON-LD Organization sitewide (schema.org) con name/url del tenant
 *
 * Tutti i valori sono derivati da:
 *  - useTenantBrand() per il nome dell'organizzazione
 *  - window.location.origin per l'URL canonico (zero hardcoding di dominio)
 *
 * Questo mantiene la piena neutralità multi-tenant: nessuna stringa LNI
 * viene scritta in `index.html` o nei file statici. Ogni tenant ottiene
 * automaticamente i propri metadati corretti.
 * =============================================================================
 */
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTenantBrand } from "@/hooks/useTenantBrand";

export function TenantSeoDefaults() {
  const { displayName, isLoaded } = useTenantBrand();
  const { pathname } = useLocation();
  // Origin dinamico: vale per qualsiasi host (lni-formazione.it,
  // anteprime lovable.app, domini di altri tenant in futuro).
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  // Finché il brand non è caricato evitiamo di iniettare "LNI" per default
  // (eviterebbe un flicker errato su tenant non-LNI).
  if (!isLoaded) return null;

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: displayName,
    url: origin || undefined,
  };
  // Le pagine senza Helmet dedicato (es. accesso) ricevono gli stessi
  // titoli dinamici della homepage; i Helmet di pagina hanno la precedenza.
  const title = `${displayName} | Piattaforma di formazione`;
  const description = `Piattaforma di formazione di ${displayName}: percorsi didattici, corsi e contenuti.`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`${origin}${pathname}`} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {/* og:site_name è dedupato da react-helmet-async sulla property */}
      <meta property="og:site_name" content={displayName} />
      <script type="application/ld+json">
        {JSON.stringify(orgJsonLd)}
      </script>
    </Helmet>
  );
}